/**
 * RunCap Service — Runway Capacity Analysis API
 *
 * Supports three calculation methods and Tri-Node Equilibrium.
 */

import api from './api';
import type {
    RunwayConfig,
    RunwayCapacityResult,
    RunwayScenarioComparison,
    TriNodeResult,
} from '../types/runcap';

export const runcapService = {
    /** Calculate runway capacity (Layer 1 + Layer 2) */
    async calculate(config: RunwayConfig): Promise<RunwayCapacityResult> {
        const { data } = await api.post<RunwayCapacityResult>(
            `/runcap/calculate`,
            config
        );
        return data;
    },

    /** Full 3-layer analysis with congestion and WCF trims */
    async fullAnalysis(
        config: RunwayConfig,
        f_apron_congestion: number = 0,
        wcf_score: number = 0,
        beta_w: number = 0.3,
    ): Promise<RunwayCapacityResult> {
        const { data } = await api.post<RunwayCapacityResult>(
            `/runcap/full-analysis`,
            { config, f_apron_congestion, wcf_score, beta_w }
        );
        return data;
    },

    /** Tri-Node Equilibrium analysis (Layer 3) */
    async triNode(
        rrc: number, rac_eff: number, ptbc: number, demand: number = 0,
    ): Promise<TriNodeResult> {
        const { data } = await api.post<TriNodeResult>(
            `/runcap/tri-node`,
            { rrc, rac_eff, ptbc, demand }
        );
        return data;
    },

    /** Compare as-is vs to-be scenarios */
    async compare(asIs: RunwayConfig, toBe: RunwayConfig): Promise<RunwayScenarioComparison> {
        const { data } = await api.post<RunwayScenarioComparison>(
            `/runcap/compare`,
            { as_is: asIs, to_be: toBe }
        );
        return data;
    },

    /** Get available calculation methods */
    async getMethods(): Promise<{ code: string; name: string; description: string }[]> {
        const { data } = await api.get(`/runcap/methods`);
        return data;
    },

    /** Get available weather categories */
    async getWeatherCategories(): Promise<{ code: string; name: string; reduction_factor: string }[]> {
        const { data } = await api.get(`/runcap/weather-categories`);
        return data;
    },

    /** Get available separation standards */
    async getSeparationStandards(): Promise<{ code: string; name: string }[]> {
        const { data } = await api.get(`/runcap/separation-standards`);
        return data;
    },

    /** Save RRC results back to the project */
    async saveToProject(
        projectId: string,
        rrcTotal: number,
        perRunway: { runway_id: string; rrc: number; max_arrivals: number; max_departures: number }[]
    ): Promise<{ success: boolean; saved_rrc: number }> {
        const { data } = await api.post(`/runcap/save-results/${projectId}`, {
            rrc_total: rrcTotal,
            per_runway: perRunway,
        });
        return data;
    },
};

export default runcapService;
